import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText, ArrowLeft, LogOut, Building2, Calendar, DollarSign, AlertCircle, Download, Paperclip, Upload, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreventiviPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [preventivi, setPreventivi] = useState([]);
  const [activeTab, setActiveTab] = useState('richiesta');
  const [clienti, setClienti] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPreventivo, setEditingPreventivo] = useState(null);
  const [showCustomCategoria, setShowCustomCategoria] = useState(false);
  const [allegati, setAllegati] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [formData, setFormData] = useState({
    cliente_id: '',
    referente_id: '',
    categoria: '',
    descrizione: '',
    prezzo_manuale: '',
    data_emissione: new Date().toISOString().split('T')[0],
    data_validita_inizio: new Date().toISOString().split('T')[0],
    data_validita_fine: '',
    tipologia: 'normale',
    stato: 'richiesta',
    promemoria: {
      attivo: false,
      data_promemoria: '',
      messaggio: ''
    },
    note: '',
    richiedente_nome: '',
    richiedente_email: '',
    richiedente_telefono: ''
  });

  useEffect(() => {
    fetchPreventivi();
    fetchClienti();
    fetchUtenti();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchPreventivi = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/preventivi`, getAuthHeaders());
      setPreventivi(response.data);
    } catch (error) {
      console.error('Errore nel caricamento preventivi:', error);
      if (error.response?.status === 401) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        onLogout();
      } else if (error.response?.status === 403) {
        toast.error('Non hai i permessi per accedere a questa sezione.');
      } else {
        toast.error('Errore nel caricamento dei preventivi');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClienti = async () => {
    try {
      const response = await axios.get(`${API}/clienti`, getAuthHeaders());
      setClienti(response.data);
    } catch (error) {
      console.error('Errore nel caricamento clienti:', error);
      toast.error('Errore nel caricamento dei clienti');
    }
  };

  const fetchUtenti = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeaders());
      setUtenti(response.data);
    } catch (error) {
      console.error('Errore nel caricamento utenti:', error);
      toast.error('Errore nel caricamento degli utenti');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePromemoriaChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      promemoria: {
        ...prev.promemoria,
        [field]: value
      }
    }));
  };

  const resetForm = () => {
    setFormData({
      cliente_id: '',
      referente_id: '',
      categoria: '',
      descrizione: '',
      prezzo_manuale: '',
      data_emissione: new Date().toISOString().split('T')[0],
      data_validita_inizio: new Date().toISOString().split('T')[0],
      data_validita_fine: '',
      tipologia: 'normale',
      stato: 'richiesta',
      promemoria: {
        attivo: false,
        data_promemoria: '',
        messaggio: ''
      },
      note: '',
      richiedente_nome: '',
      richiedente_email: '',
      richiedente_telefono: ''
    });
    setEditingPreventivo(null);
    setShowCustomCategoria(false);
    setAllegati([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validazione
    if (!formData.cliente_id || !formData.categoria || !formData.prezzo_manuale || !formData.data_validita_fine) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    try {
      const payload = {
        ...formData,
        prezzo_manuale: parseFloat(formData.prezzo_manuale),
        referente_id: formData.referente_id || null  // Converti stringa vuota in null
      };

      let preventivoId;

      if (editingPreventivo) {
        // Update
        await axios.put(`${API}/preventivi/${editingPreventivo.id}`, payload, getAuthHeaders());
        preventivoId = editingPreventivo.id;
        toast.success('Preventivo aggiornato con successo');
      } else {
        // Create
        const response = await axios.post(`${API}/preventivi`, payload, getAuthHeaders());
        preventivoId = response.data.id;
        toast.success('Preventivo creato con successo');
        
        // Carica gli allegati temporanei se presenti
        const allegatiTemp = allegati.filter(a => a.isTemp);
        if (allegatiTemp.length > 0) {
          toast.info(`Caricamento ${allegatiTemp.length} allegati...`);
          
          for (const allegato of allegatiTemp) {
            try {
              const formData = new FormData();
              formData.append('file', allegato.file);

              await axios.post(
                `${API}/preventivi/${preventivoId}/allegati`,
                formData,
                {
                  ...getAuthHeaders(),
                  headers: {
                    ...getAuthHeaders().headers,
                    'Content-Type': 'multipart/form-data'
                  }
                }
              );
            } catch (uploadError) {
              console.error('Errore caricamento allegato:', uploadError);
              toast.error(`Errore caricamento ${allegato.filename}`);
            }
          }
          
          toast.success('Allegati caricati con successo');
        }
      }

      setDialogOpen(false);
      resetForm();
      fetchPreventivi();
    } catch (error) {
      console.error('Errore nel salvataggio preventivo:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio del preventivo');
    }
  };

  const handleEdit = (preventivo) => {
    setEditingPreventivo(preventivo);
    
    // Check if categoria is custom (not in standard list)
    const standardCategorie = [
      'Consulenza', 'Progettazione definitiva', 'Progettazione esecutiva',
      'Incarichi Professionali', 'EPC B2B', 'EPC B2C', 'Contratto Quadro', 'Offerte Spot'
    ];
    const isCustom = !standardCategorie.includes(preventivo.categoria);
    setShowCustomCategoria(isCustom);
    
    setFormData({
      cliente_id: preventivo.cliente_id,
      referente_id: preventivo.referente_id || '',
      categoria: preventivo.categoria,
      descrizione: preventivo.descrizione || '',
      prezzo_manuale: preventivo.prezzo_manuale.toString(),
      data_emissione: preventivo.data_emissione,
      data_validita_inizio: preventivo.data_validita_inizio,
      data_validita_fine: preventivo.data_validita_fine,
      stato: preventivo.stato,
      promemoria: preventivo.promemoria || {
        attivo: false,
        data_promemoria: '',
        messaggio: ''
      },
      note: preventivo.note || '',
      richiedente_nome: preventivo.richiedente_nome || '',
      richiedente_email: preventivo.richiedente_email || '',
      richiedente_telefono: preventivo.richiedente_telefono || ''
    });
    setAllegati(preventivo.allegati || []);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo preventivo?')) {
      return;
    }

    try {
      await axios.delete(`${API}/preventivi/${id}`, getAuthHeaders());
      toast.success('Preventivo eliminato con successo');
      fetchPreventivi();
    } catch (error) {
      console.error('Errore nell\'eliminazione preventivo:', error);
      toast.error('Errore nell\'eliminazione del preventivo');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/preventivi/export`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Crea link per download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `preventivi_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('File Excel scaricato con successo');
    } catch (error) {
      console.error('Errore export Excel:', error);
      toast.error('Errore durante l\'esportazione');
    }
  };

  const handleUploadAllegato = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingFile(true);

      // Se siamo in modifica, carica direttamente sul server
      if (editingPreventivo) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API}/preventivi/${editingPreventivo.id}/allegati`,
          formData,
          {
            ...getAuthHeaders(),
            headers: {
              ...getAuthHeaders().headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        setAllegati([...allegati, response.data.allegato]);
        toast.success('Allegato caricato con successo');
      } else {
        // Se siamo in creazione, salva il file localmente (sarà caricato dopo il salvataggio)
        const tempAllegato = {
          id: `temp-${Date.now()}`,
          filename: file.name,
          size: file.size,
          uploaded_at: new Date().toISOString(),
          file: file, // Salviamo il file object per caricarlo dopo
          isTemp: true
        };
        
        setAllegati([...allegati, tempAllegato]);
        toast.success('Allegato pronto per il caricamento (verrà salvato con il preventivo)');
      }
      
      e.target.value = '';
    } catch (error) {
      console.error('Errore upload allegato:', error);
      toast.error('Errore durante il caricamento');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDownloadAllegato = async (allegatoId, filename) => {
    try {
      const response = await axios.get(
        `${API}/preventivi/${editingPreventivo.id}/allegati/${allegatoId}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Errore download allegato:', error);
      toast.error('Errore durante il download');
    }
  };

  const handleDeleteAllegato = async (allegatoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo allegato?')) return;

    // Trova l'allegato
    const allegato = allegati.find(a => a.id === allegatoId);
    
    // Se è un allegato temporaneo (non ancora salvato), eliminalo solo localmente
    if (allegato && allegato.isTemp) {
      const nuoviAllegati = allegati.filter(a => a.id !== allegatoId);
      setAllegati(nuoviAllegati);
      toast.success('Allegato rimosso');
      return;
    }

    // Altrimenti, elimina dal server
    try {
      console.log('Eliminazione allegato:', allegatoId, 'da preventivo:', editingPreventivo?.id);
      
      const response = await axios.delete(
        `${API}/preventivi/${editingPreventivo.id}/allegati/${allegatoId}`,
        getAuthHeaders()
      );
      
      console.log('Risposta eliminazione:', response.data);

      // Aggiorna stato locale
      const nuoviAllegati = allegati.filter(a => a.id !== allegatoId);
      setAllegati(nuoviAllegati);
      
      // Aggiorna anche editingPreventivo per mantenere la sincronizzazione
      if (editingPreventivo) {
        setEditingPreventivo({
          ...editingPreventivo,
          allegati: nuoviAllegati
        });
      }
      
      toast.success('Allegato eliminato con successo');
    } catch (error) {
      console.error('Errore eliminazione allegato:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'eliminazione dell\'allegato';
      toast.error(errorMsg);
    }
  };

  const getStatoBadgeColor = (stato) => {
    const colors = {
      'bozza': 'bg-slate-100 text-slate-700',
      'inviato': 'bg-blue-100 text-blue-700',
      'accettato': 'bg-green-100 text-green-700',
      'rifiutato': 'bg-red-100 text-red-700',
      'scaduto': 'bg-orange-100 text-orange-700'
    };
    return colors[stato] || 'bg-slate-100 text-slate-700';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/')}
                className="border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex flex-wrap items-center gap-3">
                <div className="bg-amber-600 p-2 rounded-lg">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Preventivi</h1>
                  <p className="text-sm text-slate-600">Gestisci preventivi e offerte</p>
                </div>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={onLogout}
              className="border-slate-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
        {/* Tabs */}
        <div className="mb-6">
          <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('richiesta')}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'richiesta'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              🔔 Richieste ({preventivi.filter(p => p.stato.toLowerCase() === 'richiesta').length})
            </button>
            <button
              onClick={() => setActiveTab('bozza')}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'bozza'
                  ? 'text-amber-600 border-b-2 border-amber-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Bozze ({preventivi.filter(p => p.stato.toLowerCase() === 'bozza').length})
            </button>
            <button
              onClick={() => setActiveTab('in attesa')}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'in attesa'
                  ? 'text-amber-600 border-b-2 border-amber-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              In Attesa ({preventivi.filter(p => p.stato.toLowerCase() === 'in attesa').length})
            </button>
            <button
              onClick={() => setActiveTab('accettato')}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'accettato'
                  ? 'text-amber-600 border-b-2 border-amber-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Accettati ({preventivi.filter(p => p.stato.toLowerCase() === 'accettato').length})
            </button>
            <button
              onClick={() => setActiveTab('rifiutato')}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'rifiutato'
                  ? 'text-amber-600 border-b-2 border-amber-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Rifiutati ({preventivi.filter(p => p.stato.toLowerCase() === 'rifiutato').length})
            </button>
            <button
              onClick={() => setActiveTab('scaduto')}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'scaduto'
                  ? 'text-red-600 border-b-2 border-red-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              ⚠️ Scaduti ({preventivi.filter(p => {
                if (p.stato.toLowerCase() === 'accettato') return false;
                if (!p.data_validita_fine) return false;
                return new Date(p.data_validita_fine) < new Date();
              }).length})
            </button>
          </div>
        </div>

        {/* Action Bar */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 capitalize">
              Preventivi {activeTab}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              {preventivi.filter(p => p.stato.toLowerCase() === activeTab.toLowerCase()).length} preventivo/i
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button 
              className="bg-green-600 hover:bg-green-700 text-white shadow-md"
              onClick={handleExportExcel}
            >
              <Download className="w-4 h-4 mr-2" />
              Esporta Excel
            </Button>

            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Preventivo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingPreventivo ? 'Modifica Preventivo' : 'Nuovo Preventivo'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Cliente */}
                <div className="space-y-2">
                  <Label htmlFor="cliente_id" className="required">Cliente</Label>
                  <Select
                    value={formData.cliente_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, cliente_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona cliente..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clienti.map(cliente => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {cliente.numero_ms} - {cliente.nome_azienda}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Referente */}
                <div className="space-y-2">
                  <Label htmlFor="referente_id">Referente Redazione</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.referente_id}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, referente_id: value }))}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Seleziona referente..." />
                      </SelectTrigger>
                      <SelectContent>
                        {utenti && utenti.map(utente => (
                          <SelectItem key={utente.id} value={utente.id}>
                            {utente.nome || ''} {utente.cognome || ''} {utente.ruolo ? `- ${utente.ruolo}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.referente_id && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setFormData(prev => ({ ...prev, referente_id: '' }))}
                        title="Rimuovi referente"
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                </div>

                {/* Richiedente Preventivo */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                  <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Dati Richiedente Preventivo
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Nome Richiedente */}
                    <div className="space-y-2">
                      <Label htmlFor="richiedente_nome">Nome Richiedente</Label>
                      <Input
                        id="richiedente_nome"
                        name="richiedente_nome"
                        value={formData.richiedente_nome}
                        onChange={handleInputChange}
                        placeholder="Mario Rossi"
                      />
                    </div>

                    {/* Email Richiedente */}
                    <div className="space-y-2">
                      <Label htmlFor="richiedente_email">Email</Label>
                      <Input
                        id="richiedente_email"
                        name="richiedente_email"
                        type="email"
                        value={formData.richiedente_email}
                        onChange={handleInputChange}
                        placeholder="mario.rossi@example.com"
                      />
                    </div>

                    {/* Telefono Richiedente */}
                    <div className="space-y-2">
                      <Label htmlFor="richiedente_telefono">Telefono</Label>
                      <Input
                        id="richiedente_telefono"
                        name="richiedente_telefono"
                        type="tel"
                        value={formData.richiedente_telefono}
                        onChange={handleInputChange}
                        placeholder="+39 333 1234567"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Categoria */}
                  <div className="space-y-2">
                    <Label htmlFor="categoria" className="required">Tipologia</Label>
                    {showCustomCategoria ? (
                      <div className="flex gap-2">
                        <Input
                          value={formData.categoria}
                          onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                          placeholder="Inserisci tipologia personalizzata"
                          required
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowCustomCategoria(false);
                            setFormData(prev => ({ ...prev, categoria: '' }));
                          }}
                        >
                          ✕
                        </Button>
                      </div>
                    ) : (
                      <Select
                        value={formData.categoria}
                        onValueChange={(value) => {
                          if (value === 'altro') {
                            setShowCustomCategoria(true);
                            setFormData(prev => ({ ...prev, categoria: '' }));
                          } else {
                            setFormData(prev => ({ ...prev, categoria: value }));
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona tipologia" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Consulenza">Consulenza</SelectItem>
                          <SelectItem value="Progettazione definitiva">Progettazione definitiva</SelectItem>
                          <SelectItem value="Progettazione esecutiva">Progettazione esecutiva</SelectItem>
                          <SelectItem value="Incarichi Professionali">Incarichi Professionali</SelectItem>
                          <SelectItem value="EPC B2B">EPC B2B</SelectItem>
                          <SelectItem value="EPC B2C">EPC B2C</SelectItem>
                          <SelectItem value="Contratto Quadro">Contratto Quadro</SelectItem>
                          <SelectItem value="Offerte Spot">Offerte Spot</SelectItem>
                          <SelectItem value="altro">+ Altra tipologia...</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Prezzo */}
                  <div className="space-y-2">
                    <Label htmlFor="prezzo_manuale" className="required">Prezzo (€)</Label>
                    <Input
                      id="prezzo_manuale"
                      name="prezzo_manuale"
                      type="number"
                      step="0.01"
                      value={formData.prezzo_manuale}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                {/* Descrizione */}
                <div className="space-y-2">
                  <Label htmlFor="descrizione">Descrizione</Label>
                  <Textarea
                    id="descrizione"
                    name="descrizione"
                    value={formData.descrizione}
                    onChange={handleInputChange}
                    placeholder="Descrizione del preventivo..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Data Emissione */}
                  <div className="space-y-2">
                    <Label htmlFor="data_emissione">Data Emissione</Label>
                    <Input
                      id="data_emissione"
                      name="data_emissione"
                      type="date"
                      value={formData.data_emissione}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Data Validità Inizio */}
                  <div className="space-y-2">
                    <Label htmlFor="data_validita_inizio">Validità Inizio</Label>
                    <Input
                      id="data_validita_inizio"
                      name="data_validita_inizio"
                      type="date"
                      value={formData.data_validita_inizio}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Data Validità Fine */}
                  <div className="space-y-2">
                    <Label htmlFor="data_validita_fine" className="required">Validità Fine</Label>
                    <Input
                      id="data_validita_fine"
                      name="data_validita_fine"
                      type="date"
                      value={formData.data_validita_fine}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Tipologia Preventivo */}
                  <div className="space-y-2">
                    <Label htmlFor="tipologia">Tipologia Preventivo</Label>
                    <Select
                      value={formData.tipologia}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, tipologia: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normale">📄 Normale</SelectItem>
                        <SelectItem value="accordo_quadro">📋 Accordo Quadro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Stato */}
                  <div className="space-y-2">
                    <Label htmlFor="stato">Stato</Label>
                    <Select
                      value={formData.stato}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, stato: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="richiesta">🔔 Richiesta</SelectItem>
                        <SelectItem value="bozza">Bozza</SelectItem>
                        <SelectItem value="in attesa">In Attesa</SelectItem>
                        <SelectItem value="accettato">Accettato</SelectItem>
                        <SelectItem value="rifiutato">Rifiutato</SelectItem>
                        <SelectItem value="scaduto">Scaduto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Promemoria */}
                <div className="space-y-4 border rounded-lg p-4 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="promemoria_attivo"
                      checked={formData.promemoria.attivo}
                      onCheckedChange={(checked) => handlePromemoriaChange('attivo', checked)}
                    />
                    <Label htmlFor="promemoria_attivo" className="font-semibold">
                      Attiva Promemoria
                    </Label>
                  </div>

                  {formData.promemoria.attivo && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="data_promemoria">Data Promemoria</Label>
                        <Input
                          id="data_promemoria"
                          type="date"
                          value={formData.promemoria.data_promemoria}
                          onChange={(e) => handlePromemoriaChange('data_promemoria', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="messaggio_promemoria">Messaggio</Label>
                        <Textarea
                          id="messaggio_promemoria"
                          value={formData.promemoria.messaggio}
                          onChange={(e) => handlePromemoriaChange('messaggio', e.target.value)}
                          placeholder="Messaggio del promemoria..."
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Note */}
                <div className="space-y-2">
                  <Label htmlFor="note">Note</Label>
                  <Textarea
                    id="note"
                    name="note"
                    value={formData.note}
                    onChange={handleInputChange}
                    placeholder="Note aggiuntive..."
                    rows={3}
                  />
                </div>

                {/* Allegati */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Paperclip className="w-5 h-5" />
                      Allegati
                    </Label>
                    <label htmlFor="file-upload">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingFile}
                        onClick={() => document.getElementById('file-upload').click()}
                        className="cursor-pointer"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingFile ? 'Caricamento...' : 'Carica File'}
                      </Button>
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      onChange={handleUploadAllegato}
                      className="hidden"
                    />
                  </div>

                  {allegati.length > 0 ? (
                    <div className="space-y-2">
                      {allegati.map((allegato) => (
                        <div
                          key={allegato.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <FileText className="w-5 h-5 text-slate-400" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-900">{allegato.filename}</p>
                              <p className="text-xs text-slate-500">
                                {(allegato.size / 1024).toFixed(2)} KB - {new Date(allegato.uploaded_at).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {editingPreventivo && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadAllegato(allegato.id, allegato.filename)}
                                className="hover:bg-blue-50 text-blue-600"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAllegato(allegato.id)}
                              className="hover:bg-red-50 text-red-600"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">Nessun allegato caricato</p>
                  )}
                </div>

                {/* Actions DENTRO IL FORM */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}
                  >
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                    {editingPreventivo ? 'Aggiorna' : 'Crea'} Preventivo
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-md border border-slate-200">
          {loading ? (
            <div className="p-8 text-center text-slate-600">
              Caricamento preventivi...
            </div>
          ) : preventivi.filter(p => p.stato.toLowerCase() === activeTab.toLowerCase()).length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 capitalize">
                Nessun preventivo {activeTab}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                I preventivi con stato "{activeTab}" appariranno qui
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numero</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Referente</TableHead>
                  <TableHead>Tipologia</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Prezzo</TableHead>
                  <TableHead>Validità</TableHead>
                  {activeTab === 'scaduto' && <TableHead className="text-center">Giorni Ritardo</TableHead>}
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-center">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preventivi.filter(p => {
                  if (activeTab === 'scaduto') {
                    // Preventivi scaduti: non accettati e con data validità fine passata
                    if (p.stato.toLowerCase() === 'accettato') return false;
                    if (!p.data_validita_fine) return false;
                    return new Date(p.data_validita_fine) < new Date();
                  }
                  return p.stato.toLowerCase() === activeTab.toLowerCase();
                }).map((preventivo) => (
                  <TableRow key={preventivo.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {preventivo.numero_preventivo}
                        {preventivo.tipologia === 'accordo_quadro' && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium" title="Accordo Quadro">
                            📋 AQ
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        {preventivo.cliente_nome}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {preventivo.referente_nome || '-'}
                    </TableCell>
                    <TableCell>{preventivo.categoria}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-xs">
                      <div className="line-clamp-2" title={preventivo.descrizione || '-'}>
                        {preventivo.descrizione || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-green-700">
                      {formatCurrency(preventivo.prezzo_manuale)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="w-3 h-3 text-slate-400" />
                        {formatDate(preventivo.data_validita_inizio)} - {formatDate(preventivo.data_validita_fine)}
                      </div>
                    </TableCell>
                    {activeTab === 'scaduto' && (
                      <TableCell className="text-center">
                        {(() => {
                          const oggi = new Date();
                          const dataScadenza = new Date(preventivo.data_validita_fine);
                          const diffTime = oggi - dataScadenza;
                          const giorniRitardo = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          return (
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              giorniRitardo > 30 ? 'bg-red-100 text-red-700' :
                              giorniRitardo > 7 ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              +{giorniRitardo} giorni
                            </span>
                          );
                        })()}
                      </TableCell>
                    )}
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatoBadgeColor(preventivo.stato)}`}>
                        {preventivo.stato.charAt(0).toUpperCase() + preventivo.stato.slice(1)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-2">
                        {/* Edit button: visibile a admin/master o al referente del preventivo */}
                        {(user.ruolo === 'admin' || user.ruolo === 'master' || preventivo.referente_id === user.id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(preventivo)}
                            className="hover:bg-blue-50"
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}
                        {/* Delete button: visibile solo ad admin */}
                        {user.ruolo === 'admin' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(preventivo.id)}
                            className="hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreventiviPage;
